import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Chip,
  Button,
  TextField,
  InputAdornment,
  Alert,
  CircularProgress,
  Stack,
  useTheme,
  Collapse,
  IconButton
} from '@mui/material';
import { Search, Save, ChevronDown, ChevronRight, Settings, CirclePlus } from 'lucide-react';
import { useGlobalConfig } from 'context/GlobalContext';
import { PipelineMasterApis } from 'apis/pipelineAPI';
import { DAPipelineMaster, DAPipelineMasterQueryParams } from 'types/apis/pipelineInterfaces';

export const PipelineMaster = () => {
  const theme = useTheme();
  const { rc } = useGlobalConfig();
  
  // State management
  const [masterData, setMasterData] = useState<DAPipelineMaster[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedProducts, setExpandedProducts] = useState<Set<string>>(new Set());

  // Fetch data from API
  const fetchMasterData = async (search: string = '') => {
    setLoading(true);
    setError(null);
    
    try {
      const queryParams: DAPipelineMasterQueryParams = {
        sort_by: 'name',
        sort_order: 'asc'
      };

      // Add search filters if provided
      if (search.trim()) {
        // Search in name, classification, product, and features fields
        queryParams.name = search;
        queryParams.classification = search;
        queryParams.product = search;
        queryParams.features = search;
      }

      const response = await PipelineMasterApis.getDAPipelineMaster(rc, queryParams);
      
    //   console.log('Pipeline API Response:', response); // Debug log
      
      if (response.error) {
        setError(response.error || 'Failed to fetch pipeline master data');
      } else {
        // Handle the response format {"data": []}
        console.log('Pipeline API Response:', response?.data); // Debug log
        setMasterData(response.data?.data);
        
        // Expand only the first product by default
        if (masterData.length > 0) {
          const firstProduct = masterData[0].product || 'Unknown Product';
          setExpandedProducts(new Set([firstProduct]));
        }
      }
    } catch (err: any) {
      setError(err.message || 'An unexpected error occurred');
    } finally {
      setLoading(false);
    }
  };

  // Initial data load
  useEffect(() => {
    fetchMasterData(searchQuery);
  }, []);

  // Debounced search effect
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      // For search, we'll use client-side filtering instead of API calls
      // This provides instant results and reduces API load
      // Client-side filtering will handle showing/hiding results
    }, 300); // 300ms debounce

    return () => clearTimeout(timeoutId);
  }, [searchQuery]);

  // Handle search button click
  const handleSearch = () => {
    // With client-side filtering, we don't need to refetch data
    // The search will be handled by the groupedData useMemo
  };

  // Handle search on Enter key
  const handleSearchKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter') {
      handleSearch();
    }
  };

  // Clear search
  const clearSearch = () => {
    setSearchQuery('');
  };

  // Parse comma-separated values into chips
  const renderChips = (values: string[] | undefined | null, color: 'primary' | 'secondary' | 'success' = 'primary') => {
    if (!values) return null;
    
    return (
      <Stack direction="row" spacing={0.5} flexWrap="wrap">
        {values.map((value, index) => (
          <Chip
            key={index}
            label={value.trim()}
            size="small"
            color={color}
            variant="outlined"
            sx={{ fontSize: '0.75rem', height: '24px', fontWeight: 600 }}
          />
        ))}
      </Stack>
    );
  };

  // Group data by product with client-side filtering
  const groupedData = React.useMemo(() => {
    const groups: { [key: string]: DAPipelineMaster[] } = {};
    
    // Ensure masterData is an array
    const safeMasterData = Array.isArray(masterData) ? masterData : [];
    console.log('Safe Master Data:', safeMasterData);
    
    // Filter data based on search query (client-side fallback)
    const filteredData = safeMasterData.filter((item: DAPipelineMaster) => {
      if (!searchQuery.trim()) return true;
      
      const searchLower = searchQuery.toLowerCase();
      return (
        (item.name || '').toLowerCase().includes(searchLower) ||
        (item.classification || '').toLowerCase().includes(searchLower) ||
        (item.product || '').toLowerCase().includes(searchLower) ||
        (item.features || []).some(feature => feature.toLowerCase().includes(searchLower)) ||
            (item.customer_type || []).some(type => type.toLowerCase().includes(searchLower)) ||
            (item.fuel_type || []).some(type => type.toLowerCase().includes(searchLower)) ||
            (item.meter_type || []).some(type => type.toLowerCase().includes(searchLower))
      );
    });

    console.log('Filtered Data:', filteredData);
    
    filteredData.forEach((item: DAPipelineMaster) => {
      const productName = item.product || 'Unknown Product';
      if (!groups[productName]) {
        groups[productName] = [];
      }
      groups[productName].push(item);
    });

    console.log('Groups:', groups);
    
    return groups;
  }, [masterData, searchQuery]);


  // Toggle product expansion
  const toggleProductExpansion = (productName: string) => {
    setExpandedProducts(prev => {
      const newSet = new Set(prev);
      if (newSet.has(productName)) {
        newSet.delete(productName);
      } else {
        newSet.add(productName);
      }
      return newSet;
    });
  };

  return (
    <Box sx={{ p: 3, bgcolor: 'background.default', minHeight: '100vh' }}>
      <Box sx={{ maxWidth: 1400, mx: 'auto' }}>

        {/* Error Alert */}
        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {/* Data Table */}
        <Card sx={{ borderRadius: 2, p:2, textAlign:'left' }}>
           {/* Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Box>
            <Typography variant="h4" component="h1" sx={{ fontWeight: 600, color: 'primary.main', mb: 1, display: 'flex', alignItems: 'center' }}>
              <Settings size={24} style={{ stroke: theme.palette.primary.main, marginRight: 10 }} />
               Pipeline Master
            </Typography>
            <Typography variant="subtitle2" color="text.secondary">
              Manage pipeline components and their configurations across different customer segments.
            </Typography>
          </Box>
          
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button
              variant="outlined"
              startIcon={<Save size={16} />}
              sx={{ 
                color: 'text.secondary',
                borderColor: 'divider',
                '&:hover': {
                  borderColor: 'primary.main',
                  color: 'primary.main',
                }
              }}
            >
              Save All Changes
            </Button>
            <Button
              variant="contained"
              startIcon={<CirclePlus style={{ stroke: theme.palette.primary.contrastText }} size={16} />}
              sx={{
                bgcolor: 'primary.main',
                '&:hover': {
                  bgcolor: 'primary.dark',
                }
              }}
            >
              Add Pipeline Component
            </Button>
          </Box>
        </Box>

        {/* Search */}
        <Card sx={{ mb: 3, borderRadius: 2 }}>
          <CardContent sx={{ py: 2 , bgcolor:theme.palette.secondary.main}}>
            <TextField
              fullWidth
              placeholder="Search by name, classification, product, features, customer type, fuel type, or meter type..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyPress={handleSearchKeyPress}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search size={20} color={theme.palette.text.secondary} />
                  </InputAdornment>
                ),
                endAdornment: searchQuery && (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      onClick={clearSearch}
                      sx={{ p: 0.5 }}
                    >
                      <Typography variant="body2" sx={{ color: 'text.secondary', cursor: 'pointer' }}>
                        ✕
                      </Typography>
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            {searchQuery && (
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                Found {Object.values(groupedData).flat().length} results for "{searchQuery}"
              </Typography>
            )}
          </CardContent>
        </Card>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              <CardContent sx={{ p: 0 }}>
                {Object.keys(groupedData).length === 0 ? (
                  <Box sx={{ textAlign: 'center', py: 8 }}>
                    <Typography variant="body1" color="text.secondary">
                      {searchQuery ? 
                        `No results found for "${searchQuery}". Try adjusting your search criteria.` : 
                        'No pipeline master data available. Try adjusting your search criteria.'
                      }
                    </Typography>
                    {searchQuery && (
                      <Button 
                        variant="outlined" 
                        onClick={clearSearch}
                        sx={{ mt: 2 }}
                      >
                        Clear Search
                      </Button>
                    )}
                  </Box>
                ) : (
                  <Box>
                    {Object.entries(groupedData).map(([productName, items]: [string, DAPipelineMaster[]]) => {
                      const isExpanded = expandedProducts.has(productName);
                      
                      return (
                        <Box key={productName} sx={{ mb: 2 }}>
                          {/* Product Header - Clickable */}
                          <Box 
                            sx={{ 
                              display: 'flex',
                              alignItems: 'center',
                              gap: 1,
                              p: 2,
                              bgcolor: theme.palette.secondary.main,
                              border: '1px solid',
                              borderColor: theme.palette.divider,
                              borderRadius: 1,
                              cursor: 'pointer',
                              '&:hover': { opacity: 0.9 }
                            }}
                            onClick={() => toggleProductExpansion(productName)}
                          >
                            <IconButton size="small" sx={{ p: 0 }}>
                              {isExpanded ? 
                                <ChevronDown size={18} color={theme.palette.text.primary} /> : 
                                <ChevronRight size={18} color={theme.palette.text.primary} />
                              }
                            </IconButton>
                            <Typography variant="h6" sx={{ fontWeight: 600, color: 'text.primary', textTransform: 'uppercase' }}>
                              {productName} ({items.length})
                            </Typography>
                          </Box>
                          
                          {/* Collapsible Content */}
                          <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                            <Box sx={{ 
                              border: '1px solid',
                              borderColor: theme.palette.divider,
                              borderTop: 'none',
                              borderRadius: '0 0 4px 4px'
                            }}>
                              {/* Table Header */}
                              <Box sx={{ 
                                display: 'grid',
                                gridTemplateColumns: '2fr 1fr 1fr 1fr 1fr 1fr 100px',
                                gap: 2,
                                px: 3,
                                py: 2,
                                bgcolor: theme.palette.secondary.main,
                                borderBottom: `1px solid ${theme.palette.divider}`,
                                alignItems: 'center'
                              }}>
                                <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'text.primary' }}>
                                  Component Name
                                </Typography>
                                <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'text.primary' }}>
                                  Classification
                                </Typography>
                                <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'text.primary' }}>
                                  Customer Types
                                </Typography>
                                <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'text.primary' }}>
                                  Fuel Types
                                </Typography>
                                <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'text.primary' }}>
                                  Meter Types
                                </Typography>
                                <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'text.primary' }}>
                                  Features
                                </Typography>
                                <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'text.primary' }}>
                                  Actions
                                </Typography>
                              </Box>
                              
                              {/* Table Rows */}
                              {items.map((item: DAPipelineMaster, index: number) => (
                                <Box 
                                  key={item.id}
                                  sx={{ 
                                    display: 'grid',
                                    gridTemplateColumns: '2fr 1fr 1fr 1fr 1fr 1fr 100px',
                                    gap: 2,
                                    px: 3,
                                    py: 2,
                                    bgcolor: theme.palette.secondary.main,
                                    alignItems: 'center',
                                    borderBottom: index < items.length - 1 ? `1px solid ${theme.palette.divider}` : 'none',
                                    '&:hover': {
                                      opacity: .9
                                    }
                                  }}
                                >
                                  <Box>
                                    <Typography variant="body2" sx={{ fontWeight: 500, color: 'text.primary' }}>
                                      {item.name}
                                    </Typography>
                                    {item.file_text && (
                                      <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                                        {item.file_text}
                                      </Typography>
                                    )}
                                  </Box>
                                  <Box>
                                    <Typography variant="body2" sx={{ fontWeight: 500, color: 'text.primary' }}>
                                      {item.classification}
                                    </Typography>
                                  </Box>
                                  <Box>
                                    {renderChips(item.customer_type, 'primary')}
                                  </Box>
                                  <Box>
                                    {renderChips(item.fuel_type, 'secondary')}
                                  </Box>
                                  <Box>
                                    {renderChips(item.meter_type, 'secondary')}
                                  </Box>
                                  <Box>
                                    {renderChips(item.features, 'success')}
                                  </Box>
                                  <Box>
                                    <Typography variant="body2" color="text.secondary">
                                      ...
                                    </Typography>
                                  </Box>
                                </Box>
                              ))}
                            </Box>
                          </Collapse>
                        </Box>
                      );
                    })}
                  </Box>
                )}
              </CardContent>

            </>
          )}
        </Card>
      </Box>
    </Box>
  );
};